import { Logger } from '../../../../common/utils/logger';
import { BSCIndexedDB } from '../../../../services/indexed-db-service';
import {
	type AtlBrowserStorage,
	findStorageTarget,
	WebStorageType,
} from '../../../../services/web-storage-service';
import { setItemOverride } from '../set-item-override';

// Members the Proxy replaces rather than forwarding.
const INTERCEPTED_MEMBERS = ['setItem', 'getItem', 'removeItem', 'clear', 'key', 'length'];

/**
 * Marks a storage object as already overridden by this package. Duplicate copies of the package in
 * one page (chunk skew) each capture `globalThis.localStorage` at module evaluation, so without this
 * the second copy would proxy the first copy's Proxy and run the consent pipeline — and its
 * analytics — twice per write.
 */
const BSC_STORAGE_OVERRIDE_INSTALLED = Symbol.for('atlassian.bsc.webStorageOverrideInstalled');

const isAlreadyOverridden = (value: unknown): boolean =>
	value !== null &&
	typeof value === 'object' &&
	(value as Record<symbol, unknown>)[BSC_STORAGE_OVERRIDE_INSTALLED] === true;

/**
 * A Proxy may not report a different value for a property the target exposes as a non-writable,
 * non-configurable own data property — the runtime throws `TypeError` on every such access. A real
 * `Storage` keeps its members on `Storage.prototype` and its items as writable, configurable own
 * properties, so this only happens when something has already replaced `window.localStorage` with a
 * locked-down stand-in (some third-party storage shims and test doubles do exactly that). Detect it
 * up front and keep the legacy override for those environments instead of installing a Proxy that
 * throws on ordinary reads.
 */
const canInterceptWithProxy = (storage: Storage): boolean =>
	INTERCEPTED_MEMBERS.every((member) => {
		const descriptor = Object.getOwnPropertyDescriptor(storage, member);
		return !descriptor || descriptor.configurable === true || descriptor.writable === true;
	});

const isLockedDownOnTarget = (target: Storage, property: string | symbol): boolean => {
	const descriptor = Reflect.getOwnPropertyDescriptor(target, property);
	return descriptor !== undefined && !descriptor.configurable && descriptor.writable === false;
};

/**
 * Wraps the real `Storage` object so consent checks still apply to writes while the rest of the
 * `Storage` interface keeps working.
 *
 * The legacy override (below) replaces `window.localStorage` with a plain object carrying the six
 * `Storage` operations. That silently drops every other guarantee of the interface: `length` becomes
 * a function instead of a number, stored keys stop being enumerable own properties, and
 * property-style access no longer reaches the store. Consumers that measure or enumerate storage —
 * `localStorage.length`, `Object.keys(localStorage)` — then see an empty or nonsensical view, which
 * disabled the age-based storage cleanups our products depend on and let storage fill to the browser
 * quota (COOK-628).
 *
 * Keeping the native `Storage` as the Proxy target inherits reads, removals, enumeration and brand
 * checks for free. Only writes are intercepted, and they are intercepted without relying on the
 * host's named-property-setter semantics: browsers ignore or shadow a write to `length` or to a
 * `Storage` method, whereas jsdom persists it as an item. Deciding those cases here rather than
 * forwarding them keeps behaviour identical across hosts and guarantees nothing reaches storage
 * without passing through the consent check.
 */
const createProxiedStorage = (
	bscStorage: AtlBrowserStorage,
	storageType: WebStorageType,
): Storage => {
	const nativeStorage = bscStorage.storage;
	const storagePrototype = Object.getPrototypeOf(nativeStorage);
	const interceptedSetItem = setItemOverride(bscStorage, nativeStorage.setItem, storageType);

	// The `Storage` operations keep pointing at `AtlBrowserStorage` so their existing availability
	// guards and analytics are unaffected by this Proxy. `length` is handled in the `get` trap
	// because it is an accessor on `Storage` but a method on `AtlBrowserStorage`.
	const operations = new Map<string | symbol, unknown>([
		['setItem', interceptedSetItem],
		['getItem', bscStorage.getItem.bind(bscStorage)],
		['removeItem', bscStorage.removeItem.bind(bscStorage)],
		['clear', bscStorage.clear.bind(bscStorage)],
		['key', bscStorage.key.bind(bscStorage)],
	]);

	// Properties assigned to the storage object that are NOT storage items — symbol keys, and the
	// interface's own member names. Browsers keep these as ordinary own properties on the object and
	// never write them to the store, so we hold them here instead of touching storage. Enumeration
	// deliberately does not report them: `Object.keys(localStorage)` stays exactly the stored keys.
	// They also take precedence over `operations` on read, because products legitimately wrap the
	// storage methods (Confluence's `patchLocalStorage` replaces `setItem` and delegates to whatever
	// it captured) and that has always worked against the legacy override.
	const shadowedProperties = new Map<string | symbol, unknown>();

	// A property names a storage item unless it resolves on the prototype chain (`length`, the
	// `Storage` methods, `Object.prototype` members) or is a symbol. Used by every trap that can
	// reach the store: `set`, `defineProperty` and `deleteProperty`.
	const isStorageItemKey = (property: string | symbol): property is string =>
		typeof property === 'string' && !(property in storagePrototype);

	// The consent pipeline emits analytics, and anything on that path that persists a value with
	// property syntax would re-enter this trap and recurse forever. Bound to one level instead.
	let isHandlingStorageWrite = false;
	const gateStorageWrite = (property: string, value: unknown): void => {
		if (isHandlingStorageWrite) {
			Logger.warn(
				`Dropped re-entrant ${storageType} write for '${property}' triggered from inside the consent path`,
			);
			return;
		}
		isHandlingStorageWrite = true;
		try {
			interceptedSetItem(property, String(value));
		} finally {
			isHandlingStorageWrite = false;
		}
	};

	const proxiedStorage: Storage = new Proxy(nativeStorage, {
		get: (target, property) => {
			if (property === BSC_STORAGE_OVERRIDE_INSTALLED) {
				return true;
			}

			if (property === 'length') {
				return bscStorage.length();
			}

			if (shadowedProperties.has(property)) {
				return shadowedProperties.get(property);
			}

			if (operations.has(property)) {
				return operations.get(property);
			}

			// Deliberately NOT bound to the target: binding would hand out the raw `Storage` through
			// `localStorage.valueOf()`, which is an unchecked write path. Everything that needs a real
			// `Storage` receiver is routed above, and the remainder (`Object.prototype` members, named
			// keys, `Symbol.toStringTag`) works with the Proxy as the receiver. Returning the prototype's
			// own function object also keeps `localStorage.toString === localStorage.toString`.
			return Reflect.get(target, property, target);
		},

		// `localStorage.foo = 'bar'`, `Object.assign(localStorage, …)` and `Reflect.set` all reach the
		// store natively, so they are consent-checked exactly like `setItem`.
		set: (target, property, value, receiver) => {
			// A receiver that is neither this Proxy nor the storage itself means the assignment targets
			// something further down a prototype chain (`Object.create(localStorage).foo = 'x'`), which
			// the browser also refuses to route into the store.
			if (receiver !== proxiedStorage && receiver !== target) {
				return Reflect.set(target, property, value, receiver);
			}

			if (property === 'length') {
				// Read-only accessor: browsers ignore the write in sloppy mode. Reporting success keeps
				// strict-mode assignment from throwing where the legacy override did not.
				return true;
			}

			// Reporting success for a locked-down own property on the target violates a Proxy
			// invariant. Fail the assignment exactly as the target itself would.
			if (isLockedDownOnTarget(target, property)) {
				return false;
			}

			if (!isStorageItemKey(property)) {
				shadowedProperties.set(property, value);
				return true;
			}

			gateStorageWrite(property, value);
			// Report success even when consent blocked the write, so assignment does not throw in
			// strict mode. This matches how the `setItem` override treats a blocked write.
			return true;
		},

		defineProperty: (target, property, descriptor) => {
			// Accessor descriptors on a storage key are rejected by the browser itself — forward and let
			// it throw rather than inventing a behaviour.
			if (!('value' in descriptor) || property === 'length') {
				return Reflect.defineProperty(target, property, descriptor);
			}

			// A non-configurable property must actually exist on the target for a `true` result to be
			// legal, and consent may block the write, so refuse instead of risking a `TypeError`.
			if (descriptor.configurable === false || isLockedDownOnTarget(target, property)) {
				return false;
			}

			if (!isStorageItemKey(property)) {
				shadowedProperties.set(property, descriptor.value);
				return true;
			}

			gateStorageWrite(property, descriptor.value);
			return true;
		},

		// `delete localStorage.foo` removes the item natively, so it has to reach the store — but it goes
		// through `AtlBrowserStorage.removeItem` rather than the target directly, so removals behave
		// identically however they are spelled. Removals are not consent-checked, matching `removeItem`.
		deleteProperty: (target, property) => {
			if (shadowedProperties.has(property)) {
				shadowedProperties.delete(property);
				return true;
			}

			if (isStorageItemKey(property)) {
				bscStorage.removeItem(property);
				return true;
			}

			return Reflect.deleteProperty(target, property);
		},
	});

	return proxiedStorage;
};

const applyWebStorageOverride = (
	bscStorage: AtlBrowserStorage,
	storageType: WebStorageType,
): void => {
	const caseSensitiveStorageType =
		storageType === WebStorageType.LocalStorage ? 'localStorage' : 'sessionStorage';

	if (
		isAlreadyOverridden(window[caseSensitiveStorageType]) ||
		isAlreadyOverridden(bscStorage.storage)
	) {
		Logger.debug(`${storageType} override is already installed, skipping`);
		return;
	}

	if (canInterceptWithProxy(bscStorage.storage)) {
		Object.defineProperty(window, caseSensitiveStorageType, {
			value: createProxiedStorage(bscStorage, storageType),
			writable: true,
			configurable: true,
		});
		return;
	}

	Logger.warn(
		`${storageType} cannot be proxied because its members are not configurable — falling back to the legacy override`,
	);

	const originalSetItem = bscStorage.storage.setItem;

	// As we cannot use Object.defineProperty(window[storageType], 'setItem', ...implementation)
	// we have to override the entire utility with our own edition of the webStorage class
	const overriddenStorageClass = {
		clear: bscStorage.clear.bind(bscStorage),
		key: bscStorage.key.bind(bscStorage),
		length: bscStorage.length.bind(bscStorage),
		removeItem: bscStorage.removeItem.bind(bscStorage),
		getItem: bscStorage.getItem.bind(bscStorage),
		setItem: setItemOverride(bscStorage, originalSetItem, storageType),
	};

	Object.defineProperty(window, caseSensitiveStorageType, {
		value: overriddenStorageClass,
		writable: true,
		configurable: true,
	});
};

export const createWebStoragePassthroughSync = (storageType: WebStorageType): void => {
	const bscStorage = findStorageTarget(storageType);

	if (!bscStorage.isAvailable) {
		Logger.warn(`No ${storageType} capabilities found`);
		return;
	}

	applyWebStorageOverride(bscStorage, storageType);
};

/**
 * @private
 * @deprecated Async wrapper retained only for the `platform_bsc_sync_initialize_controls` OFF path.
 * Prefer {@link createWebStoragePassthroughSync}, which installs the override on the calling tick.
 * Removed once the gate reaches 100% — see COOK-608.
 *
 * The `BSCIndexedDB` boot below is vestigial. It backed `AtlBrowserStorage.checkIfSetByPackage`,
 * which no longer reads IndexedDB — "was this key set by BSC?" is answered synchronously by
 * `ItemKeyCache`. Booting it here cost us twice: the web-storage override was skipped ENTIRELY
 * when IndexedDB was unavailable (private-browsing modes, storage-partitioned iframes), and
 * otherwise its install was delayed by a real `indexedDB.open` raced against a 3s timeout — a
 * window in which `setItem` bypassed consent enforcement.
 */
export const createWebStoragePassthrough = async (storageType: WebStorageType): Promise<void> => {
	const bscStorage = findStorageTarget(storageType);

	if (!bscStorage.isAvailable) {
		Logger.warn(`No ${storageType} capabilities found`);
		return;
	}

	if (!BSCIndexedDB.isAvailable) {
		Logger.warn('IndexedDB is not available');
		return;
	}

	if (!BSCIndexedDB.isBooted) {
		try {
			await BSCIndexedDB.boot();
		} catch {
			Logger.warn('IndexedDB boot failed, proceeding without IDB tracking');
		}
	}

	applyWebStorageOverride(bscStorage, storageType);
};
